import { useIsOverlayOpen } from "../../hooks/hookstate/useIsOverlayOpen";
import { Flex } from "@chakra-ui/react";
import { useReadingDirection } from "../../hooks/useReadingDirection";
import { useNavigationActions } from "../../hooks/useNavigationActions";
import { ReaderHints } from "../hints/ReaderHints";
import { useReaderHints } from "../../hooks/useReaderHints";
import { READING_DIRECTION_TYPES } from "../../constants";
import { LEFT_CURSOR_URL, RIGHT_CURSOR_URL } from "./icons";
import { useEffect, useRef } from "react";

const NAV_ZONE_WIDTH = 0.35;

type NavZone = "prev" | "next" | "center";

function getNavZone(
  clickX: number,
  width: number,
  isRTL: boolean,
): NavZone {
  const leftSide = clickX < width * NAV_ZONE_WIDTH;
  const rightSide = clickX > width * (1 - NAV_ZONE_WIDTH);
  if (!leftSide && !rightSide) return "center";
  const isLeftPrev = !isRTL;
  if (leftSide) return isLeftPrev ? "prev" : "next";
  return isLeftPrev ? "next" : "prev";
}

export function ActionOverlay({ children }: { children?: React.ReactNode }) {
  const { toggleIsOverlayOpen } = useIsOverlayOpen();
  const { readingDirection } = useReadingDirection();
  const { showHints, hintsVisible, hideHints } = useReaderHints();
  const { goNext, goPrevious, hasNext, hasPrev } = useNavigationActions(true);
  const flexRef = useRef<HTMLDivElement>(null);

  const isHorizontal = readingDirection !== READING_DIRECTION_TYPES.vertical;
  const isRTL = readingDirection === READING_DIRECTION_TYPES.rightToLeft;

  // Clear any stale side-zone cursor when we flip to vertical or hints appear
  // (ReaderHints is on top and has its own cursor while visible).
  useEffect(() => {
    if (flexRef.current && (!isHorizontal || showHints)) {
      flexRef.current.style.cursor = "";
    }
  }, [isHorizontal, showHints]);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (showHints) return;

    // Let interactive elements (buttons, links) handle their own clicks
    const target = e.target as HTMLElement;
    if (target.closest('a, button, [role="button"]')) return;

    if (!isHorizontal) {
      toggleIsOverlayOpen();
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const zone = getNavZone(e.clientX - rect.left, rect.width, isRTL);

    if (zone === "prev" && hasPrev) {
      goPrevious();
      return;
    }
    if (zone === "next" && hasNext) {
      goNext();
      return;
    }
    toggleIsOverlayOpen();
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!flexRef.current) return;
    if (showHints || !isHorizontal) {
      flexRef.current.style.cursor = "";
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const zone = getNavZone(e.clientX - rect.left, rect.width, isRTL);

    let cursor = "";
    if (zone === "prev" && hasPrev) cursor = LEFT_CURSOR_URL;
    else if (zone === "next" && hasNext) cursor = RIGHT_CURSOR_URL;

    flexRef.current.style.cursor = cursor;
  };

  return (
    <Flex
      ref={flexRef}
      onClick={handleClick}
      onMouseMove={handleMouseMove}
      id="ActionOverlay"
      position="relative"
      margin="auto"
      w="100%"
      h="fit-content"
      minHeight="100%"
      bottom={0}
      left={0}
      zIndex={2}
      style={{ touchAction: "pan-x pan-y pinch-zoom" }}
    >
      {children}
      {showHints && (
        <ReaderHints
          isVisible={hintsVisible}
          onInteraction={hideHints}
        />
      )}
    </Flex>
  );
}
